import { Button, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { useFilter } from '../contexts/FilterContext';

type TopJobRolesProps = {
  onViewAllJobs?: () => void;
};

type JobRole = {
  role: string;
  sector: string;
  occupation: string;
  occupationCode: string;
  skills: string;
  employment: Record<string, string>;
};



const TopJobRoles: React.FC<TopJobRolesProps> = ({ onViewAllJobs }) => {
  const { selectedSector, selectedOccupation, timeRange, selectedTab } = useFilter();
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

const [jobRolesData, setJobRolesData] = useState<JobRole[]>([]);
const [sectors, setSectors] = useState<{ code: string; value: string }[]>([]);

useEffect(() => {
  const loadData = async () => {
    try {
      const [roles, sectorList] = await Promise.all([
        import(`../json/${selectedTab}/jobRolesData.json`),
        import(`../json/${selectedTab}/sectors.json`),
      ]);
      setJobRolesData(roles.default || []);
      setSectors(sectorList.default || []);
    } catch (error) {
      console.error(`Error loading data for tab "${selectedTab}"`, error);
      setJobRolesData([]);
      setSectors([]);
    }
  };

  loadData();
}, [selectedTab]);


const getFilteredData = () => {
  let filtered = jobRolesData;

  if (selectedSector && selectedSector !== 'All') {
    const selectedSectorName = sectors.find(s => s.code === selectedSector)?.value;

    filtered = filtered.filter(job => {
      return job.sector === selectedSectorName;
    });
  }

  if (selectedOccupation && selectedOccupation !== 'All') {
  filtered = filtered.filter(job => job.occupationCode === selectedOccupation);
}

  return filtered;
};

  const filteredData = getFilteredData().map(job => {
  const rawValue = job.employment?.[timeRange] ?? "0";
  return {
    ...job,
    employmentRaw: parseInt(rawValue, 10),
  };
});

const sortedData = [...filteredData].sort((a, b) => {
  return sortOrder === 'asc'
    ? a.employmentRaw - b.employmentRaw
    : b.employmentRaw - a.employmentRaw;
});

const displayedData = !onViewAllJobs
  ? sortedData.slice((page - 1) * itemsPerPage, page * itemsPerPage)
  : [...filteredData]
      .sort((a, b) => b.employmentRaw - a.employmentRaw)
      .slice(0, 15);


    const handleSortByEmployment = () => {
  setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
};

  return (
    <div className="flex-grow-1 border rounded p-3 bg-white" style={{ minWidth: '300px', flex: 1 }}>
      {/* Header */}
      {onViewAllJobs && <div className="d-flex justify-content-between align-items-center pb-4">
        <div>
          <h6 className="mb-1 d-flex align-items-center gap-1" style={{ fontSize: 20 }}>
            Top Job Roles
            <Tooltip title="Shows the most in-demand roles based on selected filters" arrow>
              <InfoOutlined sx={{ fontSize: 16, color: '#6b7280', cursor: 'pointer' }} />
            </Tooltip>
          </h6>
          <p className="text-muted small mb-0">Click a job title to filter the dashboard.</p>
        </div>
        <Button
          onClick={onViewAllJobs}
          variant="outlined"
          size="small"
          sx={{
            fontSize: '12px',
            textTransform: 'none',
            paddingX: 2,
            borderRadius: '20px',
            borderColor: '#D7DAE0',
            color: '#043C5B',
            '&:hover': {
              borderColor: '#043C5B',
            },
          }}
        >
          View All Jobs &nbsp;&nbsp;
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M11.5556 4.55554L17 9.99999M17 9.99999L11.5556 15.4444M17 9.99999L3 9.99999"
              stroke="#043C5B"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>
      </div>}
      <div className="table-responsive" style={{ maxHeight: onViewAllJobs ?'400px':'auto', overflowY: 'auto' }}>
        <table className="table table-sm mb-0">
          <thead>
            <tr style={{ color: '#374151', fontSize: '14px' }}>
              <th>Role</th>
              <th>Skills</th>
              <th onClick={handleSortByEmployment} style={{ cursor: 'pointer' }}>
                  {!onViewAllJobs ? `Total Employment ${ sortOrder === 'asc' ? '↑' : '↓'}`:"Total Employment"}
                </th>
            </tr>
          </thead>
          <tbody>
            {displayedData.map((job, index) => (
              <tr key={index} style={{ fontSize: '14px' }}>
                <td style={{ backgroundColor: "#fff", color: '#4B5563', fontSize: 15, padding: "10px" }}>
                  <strong>{job.role}</strong>
                </td>
                <td style={{ backgroundColor: "#fff", color: '#4B5563', fontSize: 15, padding: "10px" }}>
                  {job.skills}
                </td>
                <td style={{ backgroundColor: "#fff", color: '#4B5563', fontSize: 15, padding: "10px" }}>
                  {job.employment?.[timeRange] || 'N/A'}
                </td>
              </tr>
            ))}
            {displayedData.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted py-3 bg-white">
                  No data available for this category.
                </td>
              </tr>
            )}
          </tbody>
        </table>

{!onViewAllJobs && (
  <div className="d-flex justify-content-center mt-3">
    {(() => {
      const totalPages = Math.ceil(filteredData.length / itemsPerPage);
      const paginationItems: (number | string)[] = [];

      if (totalPages <= 5) {
        for (let i = 1; i <= totalPages; i++) {
          paginationItems.push(i);
        }
      } else {
        paginationItems.push(1); // Always show first page

        if (page > 3) {
          paginationItems.push('...');
        }

        const start = Math.max(2, page - 1);
        const end = Math.min(totalPages - 1, page + 1);

        for (let i = start; i <= end; i++) {
          paginationItems.push(i);
        }

        if (page < totalPages - 2) {
          paginationItems.push('...');
        }

        paginationItems.push(totalPages); // Always show last page
      }

      return paginationItems.map((item, idx) =>
        item === '...' ? (
          <span key={`dots-${idx}`} style={{ padding: '0 6px', color: '#6b7280' }}>…</span>
        ) : (
          <Button
            key={item}
            variant={item === page ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setPage(Number(item))}
            sx={{
              mx: 0.5,
              minWidth: 30,
              width: 30,
              height: 30,
              borderRadius: '50%',
              padding: 0,
              boxShadow: 'none',
              backgroundColor: item === page ? '#043C5B' : 'transparent',
              color: item === page ? '#fff' : '#374151',
              borderColor: '#D7DAE0',
              '&:hover': {
                backgroundColor: item === page ? '#032f49' : '#f3f3f3',
                boxShadow: 'none',
              },
            }}
          >
            {item}
          </Button>
        )
      );
    })()}
  </div>
)}




      </div>



    </div>
  );
};

export default TopJobRoles;
